import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { FirebaseError } from 'firebase/app';
import { MultiFactorResolver, TotpMultiFactorGenerator } from 'firebase/auth';
import { getErrorMessageFromAuthErrorCode } from '../../../api/firebase/auth/error';
import {
  MFA_SESSION_EXPIRED_AUTH_ERROR_CODES,
  MFA_TOTP_CODE_LENGTH,
} from '../../../constants/enum';
import { gray40, primaryButtonBackgroundColor, red20, red50 } from '../../../constants/colors';
import { dialogText, dialogTitle } from '../../../constants/location';
import CustomTextfield from '../form/CustomTextfield';

const MFA_SESSION_EXPIRED_MESSAGE =
  '再認証の有効期限が切れました。お手数ですが、最初からやり直してください。';

/**
 * 再認証のペンディングセッション（第一要素の証明）の期限切れ・無効エラーか判定する（#3282）。
 * この状態は確認コードを入れ直しても解消しないため、操作のやり直しへ誘導する必要がある。
 */
const isMfaSessionExpiredError = (error: FirebaseError): boolean =>
  MFA_SESSION_EXPIRED_AUTH_ERROR_CODES.some(code => code === error.code);

interface Props {
  open: boolean;
  /**
   * 再認証（reauthenticateWithCredential）が multi-factor-auth-required を
   * 返したときの resolver
   */
  resolver: MultiFactorResolver | null;
  /** ダイアログのタイトル（既定: 二要素認証） */
  title?: string;
  /** 第二要素の解決に成功したあとの後処理（本人確認が必要な操作の実行） */
  onVerified: () => Promise<void>;
  onClose: () => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  bodyText: {
    ...dialogText,
    marginBottom: '16px',
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    marginBottom: '16px',
    padding: '8px 12px',
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  actionButton: {
    height: '44px',
    minWidth: '140px',
  },
};

/**
 * 再認証時の二要素認証ダイアログ（#3282）。
 * 認証アプリ（TOTP）を登録済みのユーザーは、パスワードだけの再認証が
 * multi-factor-auth-required で中断されるため、6 桁の確認コードで第二要素を解決する。
 *
 * ログイン時の MfaSignInDialog とは別物として用意している。あちらが提示する
 * バックアップコードは、第二要素の代わりにカスタムトークンで「そのログインだけ」を
 * 通すものであり、セッションを差し替えてしまうため本人確認（再認証）の証明にはならない。
 */
const MfaReauthDialog: React.FC<Props> = ({
  open,
  resolver,
  title = '二要素認証',
  onVerified,
  onClose,
}) => {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // 再認証のペンディングセッションが期限切れになった状態（コード再入力では解消しない）
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (open) {
      setCode('');
      setErrorMessage('');
      setSessionExpired(false);
    }
  }, [open]);

  const handleVerifyCode = async (): Promise<void> => {
    if (!resolver) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const totpHint = resolver.hints.find(
        hint => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      );
      if (!totpHint) {
        setErrorMessage('この端末で利用できる二要素認証がありません。');
        return;
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim());
      await resolver.resolveSignIn(assertion);
      // 再認証が完了したので、本人確認が必要な操作を呼び出し元で続行する
      await onVerified();
    } catch (e) {
      if (e instanceof FirebaseError) {
        if (isMfaSessionExpiredError(e)) {
          // 期限切れはやり直しでしか解消しないため、汎用エラーではなく専用の案内を出す
          setSessionExpired(true);
          setErrorMessage(MFA_SESSION_EXPIRED_MESSAGE);
          return;
        }
        // 確認コード誤りの文言は getErrorMessageFromAuthErrorCode 側で個別マッピング済み（#3282）
        setErrorMessage(getErrorMessageFromAuthErrorCode(e.code));
      } else {
        Sentry.captureException(e);
        setErrorMessage('二要素認証に失敗しました。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>{title}</Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}

      <Typography sx={styles.bodyText}>
        {sessionExpired
          ? MFA_SESSION_EXPIRED_MESSAGE
          : `本人確認のため、認証アプリに表示されている${MFA_TOTP_CODE_LENGTH}桁の確認コードを入力してください。`}
      </Typography>
      {!sessionExpired && (
        <CustomTextfield
          labelText={`確認コード（${MFA_TOTP_CODE_LENGTH}桁）`}
          inputWidth="100%"
          fontSizeIs14px
          placeholder="123456"
          value={code}
          onChange={(e): void => setCode(e.target.value)}
          formMb="0"
        />
      )}
      <Box sx={styles.dialogActions}>
        {sessionExpired ? (
          <Button onClick={onClose} sx={[primaryButtonBackgroundColor, styles.actionButton]}>
            閉じる
          </Button>
        ) : (
          <Button
            onClick={handleVerifyCode}
            disabled={submitting || code.trim().length !== MFA_TOTP_CODE_LENGTH}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.actionButton]}
          >
            確認する
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default MfaReauthDialog;
